import { Permissions, PermissionCategories } from '@/types/permissions';
import { UserRoles, type ROLE } from '@/types/user';
import { obs } from '@/utils/observability';

// Base role permissions - önce bunları tanımla
const SUPPLY_CHAIN_USER_PERMISSIONS: Permissions[] = [
  // Supply Chain için temel yetkiler - şimdilik boş, gelecekte doldurulabilir
  Permissions.DASHBOARD_READ,
  Permissions.USER_PROFILE_READ,
  Permissions.USER_PROFILE_WRITE,
];

const CARBON_ACCOUNTING_READ_ONLY_PERMISSIONS: Permissions[] = [
  // Dashboard
  Permissions.DASHBOARD_READ,

  // Basic read permissions.
  Permissions.CALCULATOR_READ,
  Permissions.ANALYSIS_READ,
  Permissions.REPORTS_READ,
  Permissions.TARGETS_READ,
  Permissions.DECARBONIZATION_READ,
  Permissions.DATA_HUB_READ,
  Permissions.GHG_REPORTS_READ,
  Permissions.SURVEY_READ,
  Permissions.COMPANY_INFO_READ,
  Permissions.USER_PROFILE_READ,

  // Only view
  Permissions.SETTINGS_READ,
];

const CARBON_ACCOUNTING_USER_PERMISSIONS: Permissions[] = [
  // All permission of READ_ONLY role.
  ...CARBON_ACCOUNTING_READ_ONLY_PERMISSIONS,

  // Write permissions
  Permissions.CALCULATOR_WRITE,
  // Facility Responsible owns the records of the facilities assigned to them,
  // so deleting their own footprint entries is part of the job. Facility scope
  // itself comes from userAssignedFacilities, not from this list.
  Permissions.CALCULATOR_DELETE,
  Permissions.CALCULATOR_BULK_UPLOAD,
  Permissions.CALCULATOR_EXPORT,
  Permissions.ANALYSIS_EXPORT,
  Permissions.REPORTS_GENERATE,
  Permissions.REPORTS_EXPORT,
  Permissions.TARGETS_WRITE,
  Permissions.DECARBONIZATION_WRITE,
  Permissions.DATA_HUB_QUERY_CREATE,
  Permissions.DATA_HUB_EXPORT,
  Permissions.GHG_REPORTS_GENERATE,
  Permissions.SURVEY_WRITE,
  Permissions.USER_PROFILE_WRITE,

  Permissions.SETTINGS_WRITE,
  Permissions.SETTINGS_FACILITIES_MANAGE,
  Permissions.SETTINGS_EMISSIONS_MANAGE,
];

const CARBON_ACCOUNTING_FACILITY_DATA_ENTRY_PERMISSIONS: Permissions[] = [
  // Read-only everywhere...
  ...CARBON_ACCOUNTING_READ_ONLY_PERMISSIONS,

  // ...except calculator data entry on the facilities assigned to the user.
  // The facility scope comes from userAssignedFacilities (the backend already
  // filters the facility list for non-privileged roles).
  Permissions.CALCULATOR_WRITE,
  Permissions.CALCULATOR_DELETE,
  Permissions.CALCULATOR_BULK_UPLOAD,
  Permissions.CALCULATOR_EXPORT,
  Permissions.USER_PROFILE_WRITE,

  // Deliberately NOT granted: SETTINGS_EMISSIONS_MANAGE — this role must not add
  // or change emission sources, only enter data for the ones already configured.
];

const PARTNER_READ_ONLY_PERMISSIONS: Permissions[] = [
  Permissions.DASHBOARD_READ,
  Permissions.REPORTS_READ,
  Permissions.ANALYSIS_READ,
  Permissions.DATA_HUB_READ,
  Permissions.USER_PROFILE_READ,
];

const PARTNER_USER_PERMISSIONS: Permissions[] = [
  ...PARTNER_READ_ONLY_PERMISSIONS,
  Permissions.REPORTS_GENERATE,
  Permissions.REPORTS_EXPORT,
  Permissions.DATA_HUB_QUERY_CREATE,
  Permissions.DATA_HUB_EXPORT,
  Permissions.USER_PROFILE_WRITE,
];

// Role-Permission Mapping System
export const ROLE_PERMISSIONS: Record<UserRoles, Permissions[]> = {
  [UserRoles.SUPPLY_CHAIN_USER]: SUPPLY_CHAIN_USER_PERMISSIONS,

  [UserRoles.SUPPLY_CHAIN_ADMIN]: [
    ...SUPPLY_CHAIN_USER_PERMISSIONS,
    // Admin yetkileri eklenebilir
  ],
  [UserRoles.CARBON_ACCOUNTING_READ_ONLY]: CARBON_ACCOUNTING_READ_ONLY_PERMISSIONS,

  [UserRoles.CARBON_ACCOUNTING_USER]: CARBON_ACCOUNTING_USER_PERMISSIONS,

  [UserRoles.CARBON_ACCOUNTING_FACILITY_DATA_ENTRY]: CARBON_ACCOUNTING_FACILITY_DATA_ENTRY_PERMISSIONS,

  [UserRoles.CARBON_ACCOUNTING_ADMIN]: [
    // All of USER's permissions.
    ...CARBON_ACCOUNTING_USER_PERMISSIONS,

    // Delete or Admin permissions. (CALCULATOR_DELETE comes from USER above.)
    Permissions.TARGETS_DELETE,
    Permissions.DECARBONIZATION_PROJECTS_MANAGE,
    Permissions.DATA_HUB_QUERY_SAVE,
    Permissions.GHG_REPORTS_SUBMIT,
    Permissions.REPORTS_SCHEDULE,
    Permissions.ANALYSIS_ADVANCED_FILTERS,
    Permissions.COMPANY_INFO_WRITE,

    // Full settings access
    Permissions.SETTINGS_USERS_MANAGE,
  ],

  [UserRoles.PARTNER_READ_ONLY]: PARTNER_READ_ONLY_PERMISSIONS,

  [UserRoles.PARTNER_USER]: PARTNER_USER_PERMISSIONS,

  [UserRoles.PARTNER_ADMIN]: [
    ...PARTNER_USER_PERMISSIONS,
    Permissions.SETTINGS_READ,
    Permissions.SETTINGS_WRITE,
  ],
  
  [UserRoles.BONO_SUPER_ADMIN]: [
    // All permissions
    ...Object.values(Permissions),
  ],
  
  [UserRoles.BONO_ACCOUNT_MANAGER]: [
    // Most permissions (expect system settings)
    ...Object.values(Permissions).filter(p => 
      p !== Permissions.ADMIN_SYSTEM_SETTINGS
    ),
  ]
};

// for cache
let permissionCache = new Map<string, Permissions[]>();

/**
 * Retrieves all permissions from the user's roles.
 *
 */
export const getUserPermissions = (userRoles: ROLE[]): Permissions[] => {
  if (!userRoles || !Array.isArray(userRoles)) return [];

  // Make a cache key.
  const cacheKey = userRoles
    .map(role => role.roleName)
    .sort()
    .join(',');

  // Check from cache
  if (permissionCache.has(cacheKey)) {
    return permissionCache.get(cacheKey) || [];
  }

  // Collect all permissions role.
  const allPermissions = new Set<Permissions>();
  
  userRoles.forEach(role => {
    // First add the direct permission from the role. ( In the feature, a permission list may come from backend)
    if (role.permissions) {
      role.permissions.forEach(permission => allPermissions.add(permission));
    }
    
    // Then add the permissions from mapping.
    const rolePermissions = ROLE_PERMISSIONS[role.roleName] || [];

    if (rolePermissions.length === 0 && !role.permissions?.length) {
      // A role name the frontend does not know grants nothing, and the UI just
      // hides every action. Surface it instead of leaving the user stranded.
      obs.message('permissions.unknownRole', 'warning', {
        roleName: role.roleName,
        roleId: role.id,
        knownRoles: Object.keys(ROLE_PERMISSIONS),
      });
    }

    rolePermissions.forEach(permission => allPermissions.add(permission));
  });

  const permissions = Array.from(allPermissions);
  
  // Save cache
  permissionCache.set(cacheKey, permissions);
  
  return permissions;
};

/**
 * Kullanıcının belirli bir permission'a sahip olup olmadığını kontrol eder
 */
export const hasPermission = (userRoles: ROLE[], permission: Permissions): boolean => {
  const userPermissions = getUserPermissions(userRoles);
  return userPermissions.includes(permission);
};

/**
 * Kullanıcının verilen permissionlardan herhangi birine sahip olup olmadığını kontrol eder
 */
export const hasAnyPermission = (userRoles: ROLE[], permissions: Permissions[]): boolean => {
  if (!permissions || permissions.length === 0) return false;
  
  const userPermissions = getUserPermissions(userRoles);
  return permissions.some(permission => userPermissions.includes(permission));
};

/**
 * Kullanıcının verilen permissionların hepsine sahip olup olmadığını kontrol eder
 */
export const hasAllPermissions = (userRoles: ROLE[], permissions: Permissions[]): boolean => {
  if (!permissions || permissions.length === 0) return true;
  
  const userPermissions = getUserPermissions(userRoles);
  return permissions.every(permission => userPermissions.includes(permission));
};

/**
 * Kullanıcının belirli bir modüle okuma erişimi olup olmadığını kontrol eder
 */
export const canReadModule = (userRoles: ROLE[], module: keyof typeof PermissionCategories): boolean => {
  const modulePermissions = [...PermissionCategories[module]] as Permissions[];
  const readPermissions = modulePermissions.filter((p: Permissions) => p.includes(':read'));

  if (readPermissions.length === 0) {
    // Modülde read permission yoksa, herhangi bir permission varlığını kontrol et
    return hasAnyPermission(userRoles, modulePermissions);
  }

  return hasAnyPermission(userRoles, readPermissions);
};

/**
 * Kullanıcının belirli bir modüle yazma erişimi olup olmadığını kontrol eder
 */
export const canWriteModule = (userRoles: ROLE[], module: keyof typeof PermissionCategories): boolean => {
  const modulePermissions = [...PermissionCategories[module]] as Permissions[];
  const writePermissions = modulePermissions.filter((p: Permissions) =>
    p.includes(':write') || p.includes(':create') || p.includes(':manage')
  );

  return hasAnyPermission(userRoles, writePermissions);
};

/**
 * Kullanıcının belirli bir modülden silme erişimi olup olmadığını kontrol eder
 */
export const canDeleteModule = (userRoles: ROLE[], module: keyof typeof PermissionCategories): boolean => {
  const modulePermissions = [...PermissionCategories[module]] as Permissions[];
  const deletePermissions = modulePermissions.filter((p: Permissions) => p.includes(':delete'));

  return hasAnyPermission(userRoles, deletePermissions);
};

/**
 * Cache'i temizler - role değişikliklerinde kullanılır
 */
export const clearPermissionCache = (): void => {
  permissionCache.clear();
};

/**
 * Kullanıcının admin yetkilerini kontrol eder
 */
export const isAdmin = (userRoles: ROLE[]): boolean => {
  const adminRoles = [
    UserRoles.CARBON_ACCOUNTING_ADMIN,
    UserRoles.PARTNER_ADMIN, 
    UserRoles.BONO_ACCOUNT_MANAGER,
    UserRoles.BONO_SUPER_ADMIN
  ];
  
  return userRoles.some(role => adminRoles.includes(role.roleName));
};

/**
 * Kullanıcının read-only olup olmadığını kontrol eder
 */
export const isReadOnly = (userRoles: ROLE[]): boolean => {
  const readOnlyRoles = [
    UserRoles.CARBON_ACCOUNTING_READ_ONLY,
    UserRoles.PARTNER_READ_ONLY
  ];
  
  return userRoles.some(role => readOnlyRoles.includes(role.roleName));
};

/**
 * Permission string'ini method name'e çevirir
 * Örnek: "calculator:read" -> "canCalculatorRead"
 */
export const permissionToMethodName = (permission: Permissions): string => {
  const parts = permission.split(':');
  if (parts.length !== 2) {
    console.warn(`[permissionToMethodName] Invalid permission format: ${permission}`);
    return permission;
  }

  const [module, action] = parts;
  const moduleName = module.replace(/_/g, '').toLowerCase();
  const actionName = action.charAt(0).toUpperCase() + action.slice(1);

  return `can${moduleName.charAt(0).toUpperCase() + moduleName.slice(1)}${actionName}`;
};

/**
 * Debug için - kullanıcının tüm permissionlarını listeler
 */
export const debugUserPermissions = (userRoles: ROLE[]): void => {
  const permissions = getUserPermissions(userRoles);
  console.group('User Permissions Debug');
  console.log('Roles:', userRoles.map(r => r.roleName));
  console.log('Permissions:', permissions);
  console.groupEnd();
};